const accountAuthError = (message, code) => {
  const error = new Error(message)
  error.code = code
  return error
}

const providerIds = user => new Set(
  (user?.providerData || [])
    .map(provider => provider?.providerId)
    .filter(Boolean)
)

export const getAccountIdentity = (user, linkedIdentities = []) => {
  const providers = new Set([...providerIds(user), ...linkedIdentities.map(identity => identity.providerId)])
  const email = [...new Set([user?.email, ...linkedIdentities.map(identity => identity.email)].filter(Boolean))].join(', ')
  const phoneNumber = [...new Set([user?.phoneNumber, ...linkedIdentities.map(identity => identity.phoneNumber)].filter(Boolean))].join(', ')

  return {
    uid: user?.uid || null,
    email,
    phoneNumber,
    emailLinked: Boolean(email),
    phoneLinked: Boolean(phoneNumber) || providers.has('phone'),
    googleLinked: providers.has('google.com'),
    appleLinked: providers.has('apple.com'),
    isAnonymous: Boolean(user?.isAnonymous) && linkedIdentities.length === 0
  }
}

export const accountLinkErrorMessage = (error, provider) => {
  const accountName = provider === 'apple' ? 'Apple' : 'Google'
  const collisionCodes = new Set([
    'auth/account-exists-with-different-credential',
    'auth/credential-already-in-use',
    'auth/email-already-in-use',
    'auth/phone-number-already-exists'
  ])

  if (collisionCodes.has(error?.code)) {
    if (provider === 'phone') {
      return 'That phone number is already linked to another IntelliKey account.'
    }
    return `That ${accountName} account is already linked to another IntelliKey account.`
  }
  if (error?.code === 'auth/provider-already-linked') {
    return provider === 'phone'
      ? 'A phone number is already linked to this IntelliKey account.'
      : `An account from ${accountName} is already linked to this IntelliKey account.`
  }
  return error?.message || `Could not link ${provider === 'phone' ? 'that phone number' : accountName}.`
}

// This deliberately has no sign-in fallback. A phone credential created from
// the Account screen may only be linked to the exact Firebase user that began
// the operation.
export const linkPhoneNumberToCurrentUser = async ({
  auth,
  phoneNumber,
  appVerifier
}) => {
  const user = auth?.currentUser
  if (!user?.uid) {
    throw accountAuthError(
      'Sign in before linking a phone number.',
      'auth/user-not-signed-in'
    )
  }

  const expectedUID = user.uid
  const confirmation = await user.linkWithPhoneNumber(phoneNumber, appVerifier)

  return {
    expectedUID,
    confirm: async verificationCode => {
      if (auth.currentUser?.uid !== expectedUID) {
        throw accountAuthError(
          'The signed-in IntelliKey account changed during phone verification.',
          'auth/user-changed'
        )
      }

      const result = await confirmation.confirm(verificationCode)
      if (!result?.user ||
          result.user.uid !== expectedUID ||
          auth.currentUser?.uid !== expectedUID) {
        throw accountAuthError(
          'Phone verification did not preserve the current IntelliKey account.',
          'auth/user-mismatch'
        )
      }
      return result
    }
  }
}
